import {Loader, Modal, Radio} from '@mantine/core';
import {useForm, zodResolver} from '@mantine/form';
import {useDisclosure} from '@mantine/hooks';
import type {AppError} from '@packages/utils';
import {useEffect, useState} from 'react';
import {
  COLOR_BLACK_1,
  COLOR_PRIMARY,
} from '../../../../config/constants/colorCodes';
import {
  type CommunityComment,
  PostDoubtCommunityCommentFormInput,
} from '../../../../core/entities/community/schema';
import {
  useGetDoubtReasonListForCommunityTopic,
  usePostDoubtCommunityComment,
} from '../../../../core/repositories/communityRepository';
import {
  showErrorSnackBar,
  showSuccessSnackBar,
} from '../../../../shared/components';
import {isRequiredDoubtReasonDetail} from '../../usecases/isRequiredDoubtReasonDetail';
import {LastConfirmModal} from './Presenter/LastConfirmModal';

type CastedError = {message: AppError};

type Props = {
  opened: boolean;
  open: () => void;
  close: () => void;
  topicId: number;
  comment: CommunityComment;
};

export const PostDoubtCommentModal: React.FC<Props> = ({
  opened,
  open,
  close,
  topicId,
  comment,
}) => {
  const [
    isLastConfirmModalOpened,
    {open: openLastConfirmModal, close: closeLastConfirmModal},
  ] = useDisclosure(false);
  const [isPostDoubtLoading, setIsPostDoubtLoading] = useState(false);

  const {data: reasonList, isLoading: isGetReasonListLoading} =
    useGetDoubtReasonListForCommunityTopic({
      topicId,
    });

  const postDoubtComment = usePostDoubtCommunityComment({
    topicId,
    seqNumber: comment.seqNumber,
    onMutateFnc() {
      setIsPostDoubtLoading(true);
    },
    onSuccessFnc() {
      showSuccessSnackBar({
        title: '送信しました',
        message: 'done',
      });
      form.reset();
    },
    onErrorFnc(error: unknown) {
      const castedError = error as CastedError;
      showErrorSnackBar({
        isNetworkError: castedError.message === 'NetworkError',
      });
    },
    onSettledFnc() {
      closeLastConfirmModal();
      setIsPostDoubtLoading(false);
    },
  });

  const form = useForm<PostDoubtCommunityCommentFormInput>({
    initialValues: {
      doubtReasonId: '',
      detail: '',
    },
    validate: zodResolver(PostDoubtCommunityCommentFormInput),
  });

  const validateReasonDetail = () => {
    if (!reasonList?.ok) {
      return false;
    }

    // DoubtReasonIdの値によって詳細な内容が必須かどうか変わるため判定
    if (
      isRequiredDoubtReasonDetail({
        selectedReasonId: Number.parseInt(form.values.doubtReasonId, 10),
        reasonList: reasonList?.value.doubtReasons,
      }) &&
      form.values.detail.length === 0
    ) {
      form.setErrors({
        detail: '詳細な内容を入力してください',
      });
      return false;
    }

    form.clearFieldError('detail');
    return true;
  };

  const onSubmit = () => {
    if (!validateReasonDetail()) {
      return;
    }

    openLastConfirmModal();
    close();
  };

  const onError = () => {
    validateReasonDetail();
  };

  const onLastConfirmSubmit = async () => {
    postDoubtComment.mutate({
      doubtReasonId: Number.parseInt(form.values.doubtReasonId, 10),
      detail: form.values.detail,
    });
  };

  const onLastConfirmClose = () => {
    closeLastConfirmModal();
    open();
  };

  useEffect(() => {
    validateReasonDetail();
  }, [form.values.doubtReasonId]);

  return (
    <>
      <Modal
        opened={opened}
        onClose={close}
        withCloseButton={false}
        size={668}
        padding={0}
        radius={12}
        centered={true}
      >
        <div className="p-24">
          <p className="text-md text-black-1 font-bold leading">
            信ぴょう性が疑われる投稿の報告
          </p>
          <p className="text-base text-notification-1 leading">
            投稿の信ぴょう性が疑われる場合のみ報告ください。
            <br />
            報告いただいた内容が投稿者に伝わることはありませんのでご安心ください。
          </p>

          <div className="mt-12">
            <p className="text-base text-black-1 font-bold leading">
              選択したコメント
            </p>
            <div className="bg-background-1 p-16 rounded-4 mt-8">
              <p className="text-base text-black-1 leading">{comment.body}</p>
            </div>
          </div>

          <form onSubmit={form.onSubmit(onSubmit, onError)} className="mt-12">
            <p className="text-base text-black-1 font-bold leading">
              選択した理由
            </p>
            <div className="bg-background-1 p-16 rounded-4 mt-8">
              {reasonList?.ok && !isGetReasonListLoading ? (
                <Radio.Group
                  id="doubtReasonId"
                  {...form.getInputProps('doubtReasonId')}
                  name="doubtReasonId"
                  error={null}
                >
                  <div className="flex flex-col text-base text-black-1">
                    {reasonList.value.doubtReasons.map((reason) => {
                      return (
                        <div
                          key={`reason-${reason.id}`}
                          className="mt-8 [&:nth-child(1)]:mt-0"
                        >
                          <Radio
                            value={String(reason.id)}
                            label={reason.reason}
                            color={COLOR_PRIMARY}
                            styles={() => ({
                              radio: {cursor: 'pointer'},
                              label: {
                                color: COLOR_BLACK_1,
                                cursor: 'pointer',
                              },
                            })}
                          />
                        </div>
                      );
                    })}
                  </div>
                </Radio.Group>
              ) : (
                <div className="flex justify-center items-center h-52">
                  <Loader color="gray" />
                </div>
              )}
            </div>
            {form.errors.doubtReasonId && (
              <p className="text-notification-1 text-sm leading mt-8">
                {form.errors.doubtReasonId}
              </p>
            )}
            <div className="mt-12">
              <p className="text-base text-black-1 font-bold leading">
                詳細な内容
              </p>
              <div className="mt-8">
                <textarea
                  id="detail"
                  {...form.getInputProps('detail')}
                  name="detail"
                  placeholder={'他サイトに記載されていた情報と異なるため'}
                  rows={5}
                  className="border-[1px] border-line border-collapse p-8 w-full rounded-[8px] text-base resize-none"
                />
                {form.errors.detail && (
                  <p className="text-notification-1 text-sm leading mt-8">
                    {form.errors.detail}
                  </p>
                )}
              </div>
            </div>
            <div className="mt-12">
              <p className="text-base text-black-1 leading">
                コミュニティの信頼性向上へのご協力ありがとうございます。
              </p>
            </div>
            <div className="pt-16 flex justify-end">
              <button
                type="button"
                className={
                  'text-base font-bold bg-black-3 text-white py-8 px-12 rounded-full mr-8 hover:opacity-80'
                }
                onClick={close}
              >
                キャンセル
              </button>
              <button
                type="submit"
                className={`text-base font-bold bg-black-3  ${Object.keys(form.errors).length > 0 ? 'text-black-1' : 'text-white'} py-8 px-12 rounded-full ${Object.keys(form.errors).length > 0 ? 'bg-disabled-1' : 'bg-primary-1'} ${Object.keys(form.errors).length > 0 ? '' : 'hover:opacity-80'}`}
                disabled={Object.keys(form.errors).length > 0}
              >
                確認画面へ
              </button>
            </div>
          </form>
        </div>
      </Modal>
      <LastConfirmModal
        opened={isLastConfirmModalOpened}
        onSubmit={onLastConfirmSubmit}
        onClose={onLastConfirmClose}
        isLoading={isPostDoubtLoading}
        isNotification={false}
      />
    </>
  );
};
